import { RefObject, useEffect, useRef } from 'react';

export interface UsePopoverDismissArgs {
	/** Whether the popover is currently open. Listeners attach only while open. */
	open: boolean;
	/** The trigger wrapper (in the normal DOM tree). */
	wrapperRef: RefObject<HTMLElement>;
	/** The portalled panel (lives under document.body, outside the wrapper). */
	panelRef: RefObject<HTMLElement>;
	/** Called on a genuine outside click (a mousedown outside both refs). */
	onDismiss: () => void;
	/** Called on scroll / resize to re-anchor the panel to the trigger. */
	onReposition: () => void;
}

/**
 * Shared open-popover behavior for the portalled Punchup popovers (`PunchupColorPicker`,
 * `PunchupCombobox`): dismiss on outside click, and **reposition** (not dismiss) on scroll/resize.
 *
 * Why reposition, not dismiss: a `position: fixed` panel portalled to `document.body` detaches from
 * its trigger when the page scrolls or the window resizes. The previous implementations *closed* the
 * panel on those events — which, inside the height-synced tixologist iframe, dismissed the popover
 * mid-keystroke: focusing the panel's input scrolls it into view (a `scroll` event), and typing
 * mutates `document.body`, which the iframe's MutationObserver answers with a height post → the host
 * resizes the iframe → a `resize` event. Re-anchoring keeps the panel attached and open; the user's
 * typing survives. Outside click remains the intended dismiss path. (Escape stays with the caller —
 * each popover closes itself on it and decides where focus lands.)
 *
 * The portalled panel is treated as "inside" so clicking/typing in it never reads as an outside click.
 * Callbacks are read through refs so listeners attach once per open, not on every render.
 */
export const usePopoverDismiss = ({
	open,
	wrapperRef,
	panelRef,
	onDismiss,
	onReposition,
}: UsePopoverDismissArgs): void => {
	const onDismissRef = useRef(onDismiss);
	const onRepositionRef = useRef(onReposition);
	onDismissRef.current = onDismiss;
	onRepositionRef.current = onReposition;

	useEffect(() => {
		if (!open) return;

		const isInside = (target: Node) => !!wrapperRef.current?.contains(target) || !!panelRef.current?.contains(target);

		const handleMouseDown = (e: MouseEvent) => {
			if (!isInside(e.target as Node)) onDismissRef.current();
		};
		const handleReposition = () => onRepositionRef.current();

		document.addEventListener('mousedown', handleMouseDown);
		// capture:true so scrolls inside any ancestor (not just the window) re-anchor the panel.
		window.addEventListener('scroll', handleReposition, true);
		window.addEventListener('resize', handleReposition);
		return () => {
			document.removeEventListener('mousedown', handleMouseDown);
			window.removeEventListener('scroll', handleReposition, true);
			window.removeEventListener('resize', handleReposition);
		};
	}, [open, wrapperRef, panelRef]);
};
